import { ComponentProps, useMemo } from 'react';
import { styled, useTheme } from 'styled-components';
import { formatSeconds } from '../../utils/helpers.ts';
import { fallbackFontFamilies } from '../../styles/consts.ts';
import { MobileText } from '../texts';

export type AnimatedCircleProps = {
  currentValue: number;
  maxValue: number;
} & Omit<ComponentProps<typeof CircleWrapper>, '$size'>;

const AnimatedCircle = ({
  currentValue,
  maxValue,
  ...props
}: AnimatedCircleProps) => {
  const theme = useTheme();
  const yellowColor = theme.getColor('starShip');
  const greenColor = theme.getColor('springGreen');
  const progress = 1 - currentValue / maxValue;

  const currentTime = useMemo(
    () => (currentValue ? formatSeconds(currentValue) : '0:00'),
    [currentValue]
  );

  return (
    <CircleWrapper {...props}>
      <Circle viewBox="0 0 100 100" $progress={progress}>
        <defs>
          <linearGradient id="gradient" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor={greenColor} />
            <stop offset="15%" stopColor={greenColor} />
            <stop offset="75%" stopColor={yellowColor} />
            <stop offset="100%" stopColor={yellowColor} />
          </linearGradient>
        </defs>
        <circle cx="50" cy="50" r="48" />
      </Circle>
      <TimeText>{currentTime}</TimeText>
    </CircleWrapper>
  );
};

const CircleWrapper = styled.div`
  width: 100px;
  height: 100px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ theme }) => theme.getUpMedia('md')} {
    width: ${({ theme }) => theme.responsive(100)};
    height: ${({ theme }) => theme.responsive(100)};
  }
`;

const Circle = styled.svg<{ $progress: number }>`
  position: absolute;
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);

  circle {
    width: 100%;
    height: 100%;
    fill: none;
    stroke-width: 5;
    stroke: url(#gradient);
    stroke-linecap: round;
    stroke-dasharray: 298.45;
    stroke-dashoffset: ${(props) => props.$progress * -298.45};
    transition: stroke-dashoffset 0.1s linear;
  }
`;

const TimeText = styled(MobileText)`
  font-size: 24px;
  line-height: 1;
  font-weight: 600;
  font-family: Inter, ${fallbackFontFamilies};
  ${({ theme }) => theme.getUpMedia('md')} {
    font-size: ${({ theme }) => theme.responsive(24)};
  }
`;

export { AnimatedCircle };
